import { useEffect, useState } from 'react'

export interface DebouncedState<T> {
    /**
     * Updates synchronously on every `setValue` call. Use this to drive a
     * controlled input so it never drops characters during fast typing.
     */
    value: T
    /**
     * Lags behind `value` by `delay` ms. Use this to trigger expensive work
     * like queries or filtering large lists.
     */
    debounced: T
    setValue: React.Dispatch<React.SetStateAction<T>>
}

/**
 * Tracks both the immediate value of a debounced piece of state and its
 * debounced counterpart. The named-object return prevents callsites from
 * accidentally swapping the two values by position.
 */
export function useDebouncedState<T = string>(initialValue: T, delay = 200): DebouncedState<T> {
    const [value, setValue] = useState(initialValue)
    const [debounced, setDebounced] = useState(initialValue)

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebounced(value)
        }, delay)

        return () => {
            clearTimeout(handler)
        }
    }, [value, delay])

    return { value, debounced, setValue }
}
